import { useEffect, useRef, useState } from 'react';

type ScrollDirection = 'left' | 'right' | null;

const DraggableScroll = ({ rootClass, refreshing, children }) => {
  const ourRef = useRef(null);
  // const { rootClass, refreshing, children } = props;
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
  const previousScrollLeft = useRef(0);

  const mouseCoords = useRef({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  // Function to detect scroll direction
  const detectScrollDirection = () => {
    if (!ourRef?.current) return;
    const slider = ourRef.current.children[0];
    const currentScrollLeft = slider.scrollLeft;
    const scrollDifference = Math.abs(currentScrollLeft - previousScrollLeft.current);

    // Only detect direction if there's meaningful movement (> 2px)
    if (scrollDifference > 2) {
      const newDirection: ScrollDirection = currentScrollLeft > previousScrollLeft.current ? 'right' : 'left';

      if (newDirection !== scrollDirection) {
        setScrollDirection(newDirection);
      }

      previousScrollLeft.current = currentScrollLeft;
    }
  };

  const handleDragStart = (e) => {
    if (!ourRef.current) return;
    const slider = ourRef?.current.children[0];
    const startX = (e.type === 'touchstart' ? e.touches[0].pageX : e.pageX) - slider.offsetLeft;
    const startY = (e.type === 'touchstart' ? e.touches[0].pageY : e.pageY) - slider.offsetTop;
    const { scrollLeft } = slider;
    const { scrollTop } = slider;
    mouseCoords.current = { startX, startY, scrollLeft, scrollTop };
    setIsMouseDown(true);
    document.body.style.cursor = 'grabbing';
  };
  const handleDragEnd = () => {
    setIsMouseDown(false);
    if (!ourRef?.current) return;
    document.body.style.cursor = 'default';
    detectScrollDirection();
  };
  const handleDragOut = () => {
    setIsMouseDown(false);
    if (!ourRef?.current) return;
    document.body.style.cursor = 'default';
    detectScrollDirection();
  };
  const handleDrag = (e) => {
    if (!isMouseDown || !ourRef?.current) return;
    const slider = ourRef.current.children[0];
    const x = (e.type === 'touchmove' ? e.touches[0].pageX : e.pageX) - slider.offsetLeft;
    const y = (e.type === 'touchmove' ? e.touches[0].pageY : e.pageY) - slider.offsetTop;
    const walkX = (x - mouseCoords.current.startX) * 1.5;
    const walkY = (y - mouseCoords.current.startY) * 1.5;
    slider.scrollLeft = mouseCoords.current.scrollLeft - walkX;
    slider.scrollTop = mouseCoords.current.scrollTop - walkY;
    detectScrollDirection();
  };

  useEffect(() => {
    ourRef?.current?.children[0].scrollTo({ left: 0 });
    if (ourRef?.current) {
      previousScrollLeft.current = 0;
      setScrollDirection(null);
    }
  }, [refreshing]);

  useEffect(() => {
    if (ourRef?.current && ourRef?.current?.children) {
      if (ourRef?.current?.children[0]) {
        previousScrollLeft.current = ourRef.current.children[0].scrollLeft;
      }
    }
  }, [ourRef?.current]);

  return (
    <div
      ref={ourRef}
      onTouchStart={handleDragStart}
      onMouseDown={handleDragStart}
      onMouseUp={handleDragEnd}
      onTouchEnd={handleDragEnd}
      onMouseMove={handleDrag}
      onTouchMove={handleDrag}
      onMouseLeave={handleDragOut}
      className={`${rootClass}`}
      data-scroll-direction={scrollDirection}
    >
      {children}
    </div>
  );
};

export default DraggableScroll;
